/**
 * Себестоимость раздела бюджета и варианты исполнения (6.6: БМ-02, 03, 04, 06, 08): база из смет,
 * варианты «свои силы» (от норм: трудоёмкость, бригада, смена, ставки), «субподряд по КП», «смешанный»,
 * расклад по видам затрат со статьями ФП, контроль полноты и выбор с обоснованием; маржа проекта.
 */
import {
  COST_TYPES,
  type CostVariant,
  MISSING_LABELS,
  type ProjectMargin,
  type SectionCost,
  VARIANT_DEFAULTS,
  VARIANT_KINDS,
  type VariantInput,
  type VariantKind,
  type VariantParams,
} from '@epc/contracts';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { tengeShort } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { OfferPicker } from '../estimates/OfferCompare';
import { ItemCostPanel } from './ItemCostPanel';
import { comma, useRates } from './RatesPanel';

export const sectionCostQuery = (lineId: string) =>
  queryOptions({
    queryKey: ['budget', 'cost', lineId],
    queryFn: () => api<SectionCost>(`/budget-lines/${lineId}/cost`),
  });

export const projectMarginQuery = (projectId: string, versionId?: string) =>
  queryOptions({
    queryKey: ['budget', 'margin', projectId, versionId ?? null],
    queryFn: () => api<ProjectMargin>(`/projects/${projectId}/margin`, { query: { versionId } }),
  });

const pct = (v: number | null) => (v === null ? '' : ` · ${v.toFixed(1)}%`);
const BTN = 'min-h-[44px] md:min-h-0';

/** Маржа проекта: сметная и плановая по выбранным вариантам, разделы без варианта (БМ-08, БМ-06) */
export function MarginPanel({ projectId, versionId }: { projectId: string; versionId?: string }) {
  const q = useQuery(projectMarginQuery(projectId, versionId));
  const [showTypes, setShowTypes] = useState(false);
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const m = q.data;
  if (!m.sectionsTotal) return null;
  const cards = [
    { label: 'Смета (текущие версии)', value: tengeShort(m.estimateAmount) },
    {
      label: 'Сметная маржа: смета − бюджет',
      value: `${tengeShort(m.estimateMargin)}${pct(m.estimateMarginPct)}`,
      tone: (m.estimateMargin ?? 0) < 0 ? 'text-danger' : 'text-success',
    },
    {
      label: `Плановая маржа по выбранным вариантам (${m.sectionsSelected} из ${m.sectionsTotal} разделов)`,
      value: `${tengeShort(m.plannedMargin)}${pct(m.plannedMarginPct)}`,
      tone: (m.plannedMargin ?? 0) < 0 ? 'text-danger' : 'text-success',
    },
    {
      label: `Плановая себестоимость${m.siteCosts ? ` (в т.ч. площадка и АУП ${tengeShort(m.siteCosts)})` : ''}`,
      value: tengeShort(m.plannedCost),
    },
    {
      label: `Контрактная маржа: ${m.contract.revenueContracts ? `договор заказчика ${tengeShort(m.contract.revenue)}` : 'выручка по смете (договора заказчика нет)'}, договоры по ${m.contract.sectionsCovered}${m.contract.sectionsPartial ? ` (+${m.contract.sectionsPartial} частично)` : ''} из ${m.sectionsTotal} разделов`,
      value: `${tengeShort(m.contract.margin)}${pct(m.contract.marginPct)}`,
      tone: (m.contract.margin ?? 0) < 0 ? 'text-danger' : 'text-success',
      title:
        'Подписанные договоры без НДС: доходный — выручка; расходные по позициям, привязанным к разделам бюджета, — себестоимость; разделы без договора — по плановой себестоимости; плюс затраты площадки. Договор на статью при выбранном варианте или на строку прежней версии бюджета считается частичным: план раздела остаётся.',
    },
    {
      label: `Прогнозная маржа: ${m.forecast.base === 'contract' ? 'контрактная' : 'плановая'}${m.forecast.changesCount ? ` + ${m.forecast.changesCount} изм. не в бюджете (выручка ${tengeShort(m.forecast.revenueDelta)}, себестоимость ${tengeShort(m.forecast.costDelta)})` : ''}${m.forecast.draftCount ? `, черновиков ${m.forecast.draftCount}` : ''}`,
      value: `${tengeShort(m.forecast.margin)}${pct(m.forecast.marginPct)}`,
      tone: (m.forecast.margin ?? 0) < 0 ? 'text-danger' : 'text-success',
      title:
        'Запросы на изменение на согласовании и утверждённые, пока не отмечены внедрёнными в сметы и бюджет (6.11); черновики в прогноз не входят.',
    },
    {
      label: `Фактическая по оплатам без НДС: получено ${tengeShort(m.actual.received)}${m.actual.receivedPct !== null ? ` (${m.actual.receivedPct} % выручки)` : ''}, оплачено ${tengeShort(m.actual.paid)}`,
      value: tengeShort(m.actual.margin),
      tone: (m.actual.margin ?? 0) < 0 ? 'text-danger' : 'text-ink',
      title:
        'Кассовый метод: поступления от заказчика минус оплаты поставщикам и подрядчикам, суммы без НДС по ставке на дату платежа.',
    },
    {
      label: `Списано со склада (кроме отчётов смены)${m.actual.consumedUnpriced ? ` (${m.actual.consumedUnpriced} строк без цены не считаются)` : ''}`,
      value: tengeShort(m.actual.consumed),
      title:
        'Материалы по учётной цене: проведённые списания и расход у получателя на проект, не сторнированные. Цена — из документа, иначе средняя цена прихода по складу.',
    },
    {
      label: 'ТО и ремонт техники проекта (по журналу ТО)',
      value: tengeShort(m.actual.machineService),
      title:
        'Стоимость записей журнала ТО и ремонтов машин проекта (раздел «Техника»). В маржу не входит: оплата исполнителям идёт через оплаты.',
    },
    {
      label: 'Начислено с площадки (люди, техника и материалы отчёта)',
      value: tengeShort(m.actual.accrued),
      title:
        'Себестоимость по начислению: оценки людей и техники из принятых отчётов плюс материалы из списаний отчёта смены. Не касса; подряд — когда появятся акты КС-2.',
    },
    {
      label: `Перерасход против нормы${m.actual.overNormLines ? ` (${m.actual.overNormLines} матер.${m.actual.overNormUnpriced ? `, ${m.actual.overNormUnpriced} без цены` : ''})` : ''}`,
      value: tengeShort(m.actual.overNorm),
      tone: (m.actual.overNorm ?? 0) > 0 ? 'text-danger' : 'text-ink',
      title:
        'СК-22: материалы с отклонением выше порога компании. Сумма = (факт − ожидание по норме) × средняя цена прихода на складах проекта. Не входит в кассовую маржу; подробности — склад → отчёт «Расход по факту против нормы».',
    },
  ];
  return (
    <Panel
      title="Маржа проекта"
      meta="БМ-08: сметная, плановая, контрактная (по подписанным договорам без НДС), прогнозная (с изменениями 6.11) и фактическая по оплатам"
      bodyClassName="p-0"
      actions={
        <button
          type="button"
          className={clsx(BTN, 'text-[13px] text-accent-text underline-offset-2 hover:underline')}
          onClick={() => setShowTypes((v) => !v)}
        >
          {showTypes ? 'скрыть виды затрат' : 'виды затрат и статьи ФП'}
        </button>
      }
    >
      <div className="grid grid-cols-2 gap-px bg-line text-[13px] sm:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-surface px-4 py-3" title={'title' in c ? c.title : undefined}>
            <span className="block text-[12px] text-ink-2">{c.label}</span>
            <span className={clsx('num block text-[15px] font-medium', c.tone ?? 'text-ink')}>{c.value}</span>
          </div>
        ))}
      </div>
      {m.forecast.byReason.length ? (
        <div className="border-t border-line px-5 py-3 text-[13px]">
          <p className="font-medium text-ink">Отклонения прогноза по причинам изменений</p>
          <ul className="mt-1 list-disc pl-5 text-ink-2">
            {m.forecast.byReason.map((r) => (
              <li key={r.reason}>
                {r.reason} — {r.count} шт.: выручка {tengeShort(r.revenueDelta)}, себестоимость{' '}
                {tengeShort(r.costDelta)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {m.incomplete.length ? (
        <div className="border-t border-line px-5 py-3 text-[13px]">
          <p className="font-medium text-ink">
            Себестоимость не завершена (БМ-06): плановая маржа считается по сумме раздела
          </p>
          <ul className="mt-1 list-disc pl-5 text-ink-2">
            {m.incomplete.map((x) => (
              <li key={x.sectionLineId}>
                {x.name} — {x.missing.map((k) => MISSING_LABELS[k] ?? k).join(', ')}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {showTypes ? (
        <table className="w-full border-t border-line text-[13px]">
          <thead>
            <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
              <th className="py-2 pr-2 pl-5 font-medium">Вид затрат</th>
              <th className="py-2 pr-2 font-medium">Статья ФП</th>
              <th className="py-2 pr-5 text-right font-medium">По выбранным вариантам</th>
            </tr>
          </thead>
          <tbody>
            {m.byCostType.map((t) => (
              <tr key={t.type} className="border-t border-line">
                <td className="py-1.5 pr-2 pl-5">{t.title}</td>
                <td className="py-1.5 pr-2 text-ink-2 [overflow-wrap:anywhere]">
                  {t.article ?? 'статью определяет финансовый менеджер'}
                </td>
                <td className="num py-1.5 pr-5 text-right">{tengeShort(t.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </Panel>
  );
}

const num = (v: number | null | undefined) => (v ? tengeShort(v) : '—');

function Breakdown({ v }: { v: CostVariant }) {
  const rows: Array<[string, number | null]> = [
    ['Материалы', v.materials],
    ['Оборудование', v.equipment],
    ['Доставка и ГСМ', v.delivery],
    ['ФОТ рабочих', v.wages],
    ['Начисления на ФОТ', v.wageTaxes],
    ['Вахта: проживание, питание, дорога', v.crewCosts],
    ['Суточные', v.perDiem],
    ['Механизмы', v.machines],
    ['Субподряд', v.subcontract],
    ['Накладные', v.overhead],
    ['Непредвиденные', v.contingency],
  ];
  return (
    <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-[13px]">
      {rows
        .filter(([, a]) => a)
        .map(([k, a]) => (
          <div key={k} className="contents">
            <dt className="text-ink-2">{k}</dt>
            <dd className="num text-right">{num(a)}</dd>
          </div>
        ))}
      <div className="contents font-medium">
        <dt>Себестоимость</dt>
        <dd className="num text-right">{num(v.total)}</dd>
      </div>
    </dl>
  );
}

type Draft = {
  kind: VariantKind;
  name: string;
  params: VariantParams;
  note: string;
  offerId?: string | null;
};

function NumField({
  label,
  value,
  onChange,
  hint,
  step = 'any',
}: {
  label: string;
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  hint?: string;
  step?: string;
}) {
  // Целые поля (бригада, дни вахты) — без дробей, иначе сервер ответит ошибкой проверки
  const parse = (raw: string) => {
    if (raw === '') return undefined;
    const n = Number(raw);
    if (!Number.isFinite(n)) return undefined;
    return step === '1' ? Math.round(n) : n;
  };
  return (
    <Field label={label} hint={hint}>
      <Input
        type="number"
        inputMode="decimal"
        step={step}
        value={value ?? ''}
        onChange={(e) => onChange(parse(e.target.value))}
      />
    </Field>
  );
}

function VariantForm({
  d,
  setD,
  base,
  offerMaterials,
  projectId,
  isNew,
}: {
  d: Draft;
  setD: (d: Draft) => void;
  /** Новый вариант: пустая ставка — ставка компании; у сохранённого — прежняя ставка варианта */
  isNew: boolean;
  base: SectionCost['base'];
  offerMaterials?: SectionCost['offerMaterials'];
  projectId: string;
}) {
  const empty = (v: number) =>
    isNew ? `пусто — ставка компании ${comma(v)}` : 'пусто — прежняя ставка варианта';
  const [pick, setPick] = useState({ requestId: '', offerId: d.offerId ?? '' });
  const [showPrices, setShowPrices] = useState(false);
  const p = d.params;
  const rates = useRates();
  const set = (patch: Partial<VariantParams>) => setD({ ...d, params: { ...p, ...patch } });
  const own = d.kind !== 'subcontract';
  const sub = d.kind !== 'own';
  const crew = p.crewSize ?? VARIANT_DEFAULTS.crewSize;
  const shift = p.shiftHours ?? VARIANT_DEFAULTS.shiftHours;
  const share = d.kind === 'mixed' ? 1 - (p.subcontractSharePct ?? 50) / 100 : 1;
  const hours = base.laborHours * share;
  const shiftsNorm = hours > 0 ? Math.round((hours / (crew * shift)) * 10) / 10 : 0;
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Вид варианта">
          <Select value={d.kind} onChange={(e) => setD({ ...d, kind: e.target.value as VariantKind })}>
            {Object.entries(VARIANT_KINDS).map(([k, t]) => (
              <option key={k} value={k}>
                {t}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Название">
          <Input value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} />
        </Field>
      </div>
      {d.kind === 'mixed' ? (
        <NumField
          label="Доля работ субподряду, %"
          value={p.subcontractSharePct ?? 50}
          onChange={(v) => set({ subcontractSharePct: v })}
          hint="Трудоёмкость, механизмы и материалы делятся по этой доле"
        />
      ) : null}
      {own ? (
        <fieldset className="flex flex-col gap-3 rounded-md border border-line p-3">
          <legend className="px-1 text-[13px] font-medium">Свои силы от норм (БМ-04)</legend>
          <p className="text-[12px] text-ink-2">
            Трудоёмкость сметы {hours.toFixed(1)} чел.-ч → {shiftsNorm} смен по норме при бригаде {crew} чел.
            и смене {shift} ч
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <NumField
              label="Бригада, чел."
              value={p.crewSize}
              onChange={(v) => set({ crewSize: v })}
              step="1"
            />
            <NumField label="Смена, ч" value={p.shiftHours} onChange={(v) => set({ shiftHours: v })} />
            <NumField
              label="Смен расчётно"
              value={p.shiftsPlanned}
              onChange={(v) => set({ shiftsPlanned: v })}
              hint={`пусто — по норме (${shiftsNorm})`}
            />
            <NumField label="Ставка, ₸/ч" value={p.hourlyRate} onChange={(v) => set({ hourlyRate: v })} />
            <NumField
              label="Начисления на ФОТ, %"
              value={p.taxesPct}
              onChange={(v) => set({ taxesPct: v })}
              hint={empty(rates.taxesPct)}
            />
            <NumField
              label="Проживание, ₸/чел.-день"
              value={p.lodgingPerDay}
              onChange={(v) => set({ lodgingPerDay: v })}
            />
            <NumField
              label="Питание, ₸/чел.-день"
              value={p.foodPerDay}
              onChange={(v) => set({ foodPerDay: v })}
            />
            <NumField
              label="Суточные, ₸/чел.-день"
              value={p.perDiemPerDay}
              onChange={(v) => set({ perDiemPerDay: v })}
            />
            <NumField
              label="Дорога, ₸/чел. за вахту"
              value={p.travelPerRotation}
              onChange={(v) => set({ travelPerRotation: v })}
            />
            <NumField
              label="Вахта, дней"
              value={p.rotationDays}
              onChange={(v) => set({ rotationDays: v })}
              step="1"
              hint={`пусто — ${VARIANT_DEFAULTS.rotationDays}`}
            />
          </div>
        </fieldset>
      ) : null}
      {sub ? (
        <fieldset className="flex flex-col gap-3 rounded-md border border-line p-3">
          <legend className="px-1 text-[13px] font-medium">Субподряд по КП</legend>
          <OfferPicker
            projectId={projectId}
            value={pick}
            onChange={(v) => {
              setPick({ requestId: v.requestId, offerId: v.offerId });
              if (!v.offerId) {
                // Выбор снят — связь с КП убирается, сумма остаётся как есть
                setD({ ...d, offerId: null });
                return;
              }
              setD({
                ...d,
                offerId: v.offerId,
                name: v.supplierName ? `Субподряд по КП ${v.supplierName}` : d.name,
                params: {
                  ...p,
                  subcontractAmount: v.total ?? p.subcontractAmount,
                  subcontractVatIncluded: v.total !== null ? true : p.subcontractVatIncluded,
                },
              });
            }}
          />
          <div className="grid gap-3 sm:grid-cols-3">
            <NumField
              label="Сумма КП, ₸"
              value={p.subcontractAmount}
              onChange={(v) => set({ subcontractAmount: v })}
            />
            <Field label="НДС в сумме КП" hint="без ответа вариант не выбирается (БМ-06)">
              <Select
                value={p.subcontractVatIncluded === undefined ? '' : p.subcontractVatIncluded ? 'yes' : 'no'}
                onChange={(e) =>
                  set({
                    subcontractVatIncluded: e.target.value === '' ? undefined : e.target.value === 'yes',
                  })
                }
              >
                <option value="">не указано</option>
                <option value="yes">с НДС (делим на 1,16)</option>
                <option value="no">без НДС</option>
              </Select>
            </Field>
            <Field label="Материалы в КП">
              <Select
                value={p.subcontractMaterialsIncluded ? 'yes' : 'no'}
                onChange={(e) => set({ subcontractMaterialsIncluded: e.target.value === 'yes' })}
              >
                <option value="no">нет — материалы наши, из сметы</option>
                <option value="yes">да — входят в сумму КП</option>
              </Select>
            </Field>
          </div>
        </fieldset>
      ) : null}
      <fieldset className="flex flex-col gap-3 rounded-md border border-line p-3">
        <legend className="px-1 text-[13px] font-medium">Материалы, доставка, накладные</legend>
        {offerMaterials ? (
          <div className="flex flex-col gap-2 text-[13px]">
            <span className="font-medium text-ink">Откуда цены материалов (БМ-11)</span>
            <div className="flex flex-col gap-1 sm:flex-row sm:flex-wrap sm:gap-4">
              {(
                [
                  ['estimate', `по смете — ${tengeShort(base.materials)}`],
                  [
                    'offers',
                    `по плановым ценам проекта, КП и поставкам — ${tengeShort(offerMaterials.amount)} (найдено ${offerMaterials.matched} из ${offerMaterials.rows.length}, ${offerMaterials.matchedShare} % по сумме)`,
                  ],
                  ['manual', 'своя сумма'],
                ] as const
              ).map(([k, label]) => (
                <label key={k} className="inline-flex min-h-[44px] items-center gap-2 md:min-h-0">
                  <input
                    type="radio"
                    name="materials-source"
                    checked={
                      (p.materialsSource ?? (p.materialsOverride != null ? 'manual' : 'estimate')) === k
                    }
                    onChange={() =>
                      set({ materialsSource: k, ...(k !== 'manual' ? { materialsOverride: null } : {}) })
                    }
                    disabled={k === 'offers' && !offerMaterials.matched}
                  />
                  {label}
                </label>
              ))}
            </div>
            {offerMaterials.rows.length ? (
              <button
                type="button"
                className="min-h-[44px] self-start text-accent-text underline md:min-h-0"
                onClick={() => setShowPrices(!showPrices)}
              >
                {showPrices ? 'Скрыть материалы' : `Материалы с ценами (${offerMaterials.rows.length})`}
              </button>
            ) : null}
            {showPrices ? (
              <ul className="divide-y divide-line rounded-md border border-line text-[12px]">
                {offerMaterials.rows.slice(0, 60).map((r) => (
                  <li
                    key={`${r.name}|${r.unit}`}
                    className="flex flex-col gap-0.5 px-3 py-2 md:flex-row md:items-center md:gap-3"
                  >
                    <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
                      {r.name}, {r.unit} × {r.quantity.toLocaleString('ru-RU')}
                    </span>
                    <span className="num text-ink-2">смета {tengeShort(r.estimatePrice)}</span>
                    <span
                      className={clsx(
                        'num',
                        r.bestPrice === null
                          ? 'text-ink-3'
                          : r.bestPrice < (r.estimatePrice ?? 0)
                            ? 'text-success'
                            : 'text-danger',
                      )}
                    >
                      {r.bestPrice === null
                        ? 'цен нет'
                        : `${r.source === 'plan' ? 'план проекта' : r.source === 'receipt' ? 'поставка' : 'КП'} ${tengeShort(r.bestPrice)}${r.supplier ? ` — ${r.supplier}` : ''}${r.date ? ` (${r.date})` : ''}`}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
        <div className="grid gap-3 sm:grid-cols-3">
          <NumField
            label="Материалы, ₸"
            value={p.materialsOverride ?? undefined}
            // Введённая сумма — источник «своя сумма»; очищенное поле возвращает источник к смете
            onChange={(v) =>
              set({
                materialsOverride: v ?? null,
                materialsSource:
                  v != null ? 'manual' : p.materialsSource === 'manual' ? 'estimate' : p.materialsSource,
              })
            }
            hint={`пусто — из сметы (${tengeShort(base.materials)})`}
          />
          <NumField
            label="Оборудование, ₸"
            value={p.equipmentOverride ?? undefined}
            onChange={(v) => set({ equipmentOverride: v ?? null })}
            hint={`пусто — из сметы (${tengeShort(base.equipment)})`}
          />
          <NumField
            label="Доставка и ГСМ, % от ТМЦ"
            value={p.deliveryPct}
            onChange={(v) => set({ deliveryPct: v })}
            hint="без доставки вариант не выбирается (БМ-06)"
          />
          <NumField
            label="Накладные, %"
            value={p.overheadPct}
            onChange={(v) => set({ overheadPct: v })}
            hint={empty(rates.overheadPct)}
          />
          <NumField
            label="Непредвиденные, %"
            value={p.contingencyPct}
            onChange={(v) => set({ contingencyPct: v })}
            hint={empty(rates.contingencyPct)}
          />
        </div>
      </fieldset>
      <Field label="Примечание">
        <Textarea rows={2} value={d.note} onChange={(e) => setD({ ...d, note: e.target.value })} />
      </Field>
    </div>
  );
}

const emptyDraft = (kind: VariantKind): Draft => ({
  kind,
  name: VARIANT_KINDS[kind],
  params: {},
  note: '',
});

/** Диалог себестоимости раздела: база, варианты, добавление и правка, выбор с обоснованием */
export function CostDialog({
  lineId,
  projectId,
  onClose,
}: {
  lineId: string;
  projectId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const q = useQuery(sectionCostQuery(lineId));
  const [editing, setEditing] = useState<{ id: string | null; d: Draft } | null>(null);
  const [choosing, setChoosing] = useState<{ id: string; reason: string } | null>(null);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['budget'] });
    qc.invalidateQueries({ queryKey: ['estimates', 'summary', projectId] });
  };
  const save = useMutation({
    mutationFn: (e: { id: string | null; d: Draft }) => {
      const body: VariantInput = {
        kind: e.d.kind,
        name: e.d.name,
        params: e.d.params,
        // Связь с КП закупок — только у субподряда; у остальных видов очищается
        offerId: e.d.kind === 'subcontract' ? (e.d.offerId ?? null) : null,
        note: e.d.note || null,
      };
      return e.id
        ? api<SectionCost>(`/cost-variants/${e.id}`, { method: 'PATCH', body })
        : api<SectionCost>(`/budget-lines/${lineId}/variants`, { body });
    },
    onSuccess: (data) => {
      qc.setQueryData(sectionCostQuery(lineId).queryKey, data);
      refresh();
      setEditing(null);
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const select = useMutation({
    mutationFn: (p: { id: string; reason: string }) =>
      api<SectionCost>(`/cost-variants/${p.id}/select`, { body: { reason: p.reason } }),
    onSuccess: (data) => {
      qc.setQueryData(sectionCostQuery(lineId).queryKey, data);
      refresh();
      setChoosing(null);
      toast('Вариант выбран: его сумма идёт в бюджет раздела');
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const del = useMutation({
    mutationFn: (id: string) => api<SectionCost>(`/cost-variants/${id}`, { method: 'DELETE' }),
    onSuccess: (data) => {
      qc.setQueryData(sectionCostQuery(lineId).queryKey, data);
      refresh();
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={q.data ? `Себестоимость: ${q.data.sectionName}` : 'Себестоимость раздела'}
      width="lg"
      description="Варианты исполнения раздела: свои силы от норм, субподряд по КП, смешанный (БМ-02…04). Вариант с пропусками не выбирается (БМ-06)."
    >
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : (
        <div className="flex flex-col gap-4 text-[13px]">
          <div className="grid grid-cols-2 gap-px bg-line sm:grid-cols-4">
            {[
              ['Смета', tengeShort(q.data.estimateAmount)],
              ['Бюджет раздела', tengeShort(q.data.budgetAmount)],
              ['Трудоёмкость', `${q.data.base.laborHours.toFixed(1)} чел.-ч`],
              [
                'Материалы и оборудование',
                tengeShort((q.data.base.materials ?? 0) + (q.data.base.equipment ?? 0)),
              ],
            ].map(([k, v]) => (
              <div key={k} className="bg-surface px-3 py-2">
                <span className="block text-[12px] text-ink-2">{k}</span>
                <span className="num block font-medium">{v}</span>
              </div>
            ))}
          </div>
          {q.data.estimates.length ? (
            <p className="text-ink-2">
              Сметы раздела: {q.data.estimates.map((e) => `${e.code} «${e.name}»`).join('; ')}
            </p>
          ) : (
            <p className="text-warning">
              К разделу не привязаны сметы — база пустая. Раздел бюджета выбирается в карточке сметы
              («Связи»).
            </p>
          )}

          {editing ? (
            <div className="rounded-md border border-line-strong p-3">
              <VariantForm
                d={editing.d}
                setD={(d) => setEditing({ ...editing, d })}
                base={q.data.base}
                offerMaterials={q.data.offerMaterials}
                projectId={projectId}
                isNew={editing.id === null}
              />
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  className={BTN}
                  variant="primary"
                  loading={save.isPending}
                  disabled={editing.d.name.trim().length < 2}
                  onClick={() => save.mutate(editing)}
                >
                  {editing.id ? 'Сохранить и пересчитать' : 'Добавить вариант'}
                </Button>
                <Button className={BTN} variant="ghost" onClick={() => setEditing(null)}>
                  Отмена
                </Button>
              </div>
            </div>
          ) : q.data.canEdit ? (
            <div className="flex flex-wrap gap-2">
              {(Object.keys(VARIANT_KINDS) as VariantKind[]).map((k) => (
                <Button
                  key={k}
                  className={BTN}
                  icon={<Plus className="size-4" />}
                  onClick={() => setEditing({ id: null, d: emptyDraft(k) })}
                >
                  {VARIANT_KINDS[k]}
                </Button>
              ))}
            </div>
          ) : null}

          {q.data.variants.length ? (
            <ul className="grid gap-3 md:grid-cols-2">
              {q.data.variants.map((v) => (
                <li
                  key={v.id}
                  className={clsx(
                    'flex flex-col gap-2 rounded-md border p-3',
                    v.isSelected ? 'border-accent bg-accent-soft/30' : 'border-line',
                  )}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{v.name}</span>
                    <Badge>{VARIANT_KINDS[v.kind]}</Badge>
                    {v.isSelected ? <Badge tone="accent">выбран</Badge> : null}
                    {v.stale ? <Badge tone="warning">сметы раздела изменились — пересчитайте</Badge> : null}
                    {v.missing.length ? (
                      <Badge tone="danger">{v.missing.map((k) => MISSING_LABELS[k] ?? k).join(', ')}</Badge>
                    ) : null}
                  </div>
                  {v.shiftsNorm !== null ? (
                    <p className="text-[12px] text-ink-2">
                      {v.laborHours.toFixed(1)} чел.-ч · смен по норме {v.shiftsNorm}
                      {v.shiftsPlanned !== null && v.shiftsPlanned !== v.shiftsNorm
                        ? `, расчётно ${v.shiftsPlanned}`
                        : ''}
                    </p>
                  ) : null}
                  <Breakdown v={v} />
                  <p
                    className={clsx('num font-medium', (v.margin ?? 0) < 0 ? 'text-danger' : 'text-success')}
                  >
                    Маржа {tengeShort(v.margin)}
                    {pct(v.marginPct)}
                  </p>
                  {v.reason ? <p className="text-[12px] text-ink-2">Обоснование выбора: {v.reason}</p> : null}
                  {v.note ? <p className="text-[12px] text-ink-2">{v.note}</p> : null}
                  {q.data.canEdit ? (
                    <div className="mt-1 flex flex-wrap gap-2">
                      {!v.isSelected ? (
                        <Button
                          className={BTN}
                          size="sm"
                          variant="primary"
                          disabled={v.missing.length > 0}
                          title={v.missing.length ? 'Заполните пропуски (БМ-06)' : undefined}
                          onClick={() => setChoosing({ id: v.id, reason: '' })}
                        >
                          Выбрать
                        </Button>
                      ) : null}
                      <Button
                        className={BTN}
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          setEditing({
                            id: v.id,
                            d: {
                              kind: v.kind,
                              name: v.name,
                              params: v.params,
                              note: v.note ?? '',
                              offerId: v.offerId,
                            },
                          })
                        }
                      >
                        Изменить
                      </Button>
                      <Button
                        className={BTN}
                        size="sm"
                        variant="ghost"
                        loading={del.isPending}
                        onClick={() => {
                          if (window.confirm(`Удалить вариант «${v.name}»?`)) del.mutate(v.id);
                        }}
                      >
                        Удалить
                      </Button>
                    </div>
                  ) : null}
                  {choosing?.id === v.id ? (
                    <div className="flex flex-col gap-2 rounded-md bg-surface-2 p-3">
                      <Field
                        label="Обоснование выбора"
                        hint="Почему этот вариант: сроки, свободная бригада, цена КП, риски"
                      >
                        <Textarea
                          rows={2}
                          value={choosing.reason}
                          onChange={(e) => setChoosing({ id: v.id, reason: e.target.value })}
                        />
                      </Field>
                      <div className="flex gap-2">
                        <Button
                          className={BTN}
                          size="sm"
                          variant="primary"
                          loading={select.isPending}
                          disabled={choosing.reason.trim().length < 5}
                          onClick={() => select.mutate(choosing)}
                        >
                          Подтвердить выбор
                        </Button>
                        <Button className={BTN} size="sm" variant="ghost" onClick={() => setChoosing(null)}>
                          Отмена
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-ink-2">
              Вариантов пока нет. Добавьте расчёт своими силами или по КП субподрядчика.
            </p>
          )}
          <ItemCostPanel lineId={lineId} />
          <p className="text-[12px] text-ink-2">
            Виды затрат и статьи ФП:{' '}
            {Object.values(COST_TYPES)
              .map((t) => `${t.title} → ${t.article ?? 'статью определяет финансовый менеджер'}`)
              .join('; ')}
            .
          </p>
        </div>
      )}
    </Dialog>
  );
}
